import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { productApi } from '@/api/commercial/products';
import { Button } from '@/components/ui/button';
import { CurrencyInput } from '@/components/ui/currency-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useProductForm } from '@/hooks/commercial/use-product-form';
import { useToast } from '@/hooks/use-toast';
import type { IProduct, IProductCategory } from '@/types/commercial/product';
import { CreateCategoryDialog } from './create-category-dialog';

interface CreateProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (product: IProduct) => void;
}

export function CreateProductDialog({ open, onOpenChange, onSuccess }: CreateProductDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    form,
    specEntries,
    fileEntries,
    addSpecEntry,
    removeSpecEntry,
    updateSpecEntry,
    addFileEntry,
    removeFileEntry,
    updateFileEntry,
    handleCategoryChange,
    getFormData,
    resetForm,
  } = useProductForm();

  const { data: categories = [] } = useQuery({
    queryKey: ['product-categories-all'],
    queryFn: () => productApi.findAllCategories(),
  });

  const handleFormSubmit = async () => {
    setIsLoading(true);
    try {
      const data = getFormData();
      const result = await productApi.create({
        name: data.name,
        category_id: data.category_id,
        color: data.color,
        priceInCents: data.priceInCents,
        description: data.description,
        spec: data.spec,
        files: data.files,
        active: data.active,
      });

      if (result.success && result.data) {
        toast({
          title: 'Sucesso',
          description: 'Produto criado com sucesso!',
        });
        resetForm();
        onOpenChange(false);
        onSuccess?.(result.data);
      } else {
        toast({
          variant: 'destructive',
          title: 'Erro',
          description: result.error?.global || 'Erro ao criar produto.',
        });

        if (result.error?.fields) {
          Object.entries(result.error.fields).forEach(([key, message]) => {
            form.setError(key as any, { message });
          });
        }
      }
    } catch {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Erro ao criar produto. Tente novamente.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      resetForm();
    }
    onOpenChange(open);
  };

  const handleCategoryCreated = (category: IProductCategory) => {
    queryClient.invalidateQueries({ queryKey: ['product-categories-all'] });
    handleCategoryChange(category);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-blue-500" />
              Novo Produto
            </DialogTitle>
            <DialogDescription>
              Preencha os dados do novo produto. O SKU será gerado automaticamente.
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome *</FormLabel>
                    <FormControl>
                      <Input placeholder="Nome do produto" {...field} maxLength={100} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="category_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Categoria *</FormLabel>
                    <div className="flex gap-2">
                      <Select
                        value={field.value}
                        onValueChange={(val) => {
                          const selected = categories.find((c) => c.id === val);
                          if (selected) {
                            handleCategoryChange(selected);
                          }
                        }}
                      >
                        <FormControl>
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Selecione uma categoria" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {categories.map((category) => (
                            <SelectItem key={category.id} value={category.id}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowCategoryDialog(true)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="color"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cor *</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input {...field} placeholder="#000000" className="pl-12" />
                          <input
                            type="color"
                            value={field.value || '#000000'}
                            onChange={(e) => field.onChange(e.target.value)}
                            className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded border border-gray-300 cursor-pointer p-0"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Preço</FormLabel>
                      <FormControl>
                        <CurrencyInput value={field.value || '0,00'} onChange={field.onChange} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descrição</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Descrição do produto..."
                        rows={3}
                        {...field}
                        maxLength={500}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Especificações</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addSpecEntry}>
                    <Plus className="h-4 w-4 mr-1" />
                    Adicionar
                  </Button>
                </div>
                <div className="space-y-2">
                  {specEntries.map((entry, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: stable ordered list, never reordered
                    <div key={index} className="flex gap-2">
                      <Input
                        placeholder="Chave"
                        value={entry.key}
                        onChange={(e) => updateSpecEntry(index, 'key', e.target.value)}
                        className="flex-1"
                      />
                      <Input
                        placeholder="Valor"
                        value={entry.value}
                        onChange={(e) => updateSpecEntry(index, 'value', e.target.value)}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeSpecEntry(index)}
                        disabled={specEntries.length === 1}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Arquivos</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addFileEntry}>
                    <Plus className="h-4 w-4 mr-1" />
                    Adicionar
                  </Button>
                </div>
                <div className="space-y-2">
                  {fileEntries.map((file, index) => (
                    <div key={file || `file-${index}`} className="flex gap-2">
                      <Input
                        placeholder="URL do arquivo"
                        value={file}
                        onChange={(e) => updateFileEntry(index, e.target.value)}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeFileEntry(index)}
                        disabled={fileEntries.length === 1}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleClose(false)}
                  disabled={isLoading}
                >
                  Cancelar
                </Button>
                <Button type="submit" className="bg-black hover:bg-gray-800" disabled={isLoading}>
                  {isLoading ? 'Salvando...' : 'Salvar'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <CreateCategoryDialog
        open={showCategoryDialog}
        onOpenChange={setShowCategoryDialog}
        onSuccess={handleCategoryCreated}
      />
    </>
  );
}
